import { useCallback, useEffect, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import type { SubscriptionDetail } from '@services/requests/housien-services/AgenciesService.subscription';

export interface UseSubscriptionDetailResult {
  data: SubscriptionDetail | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Fetches the subscription detail for an agency from the housien backend.
 *
 * Reads from the Payien-backed GET /agencies/{agencyId}/subscription endpoint.
 * Exposes isLoading (true while the first fetch is pending), isError, and
 * a refetch callback for pull-to-refresh use cases.
 */
export function useSubscriptionDetail(agencyId: string): UseSubscriptionDetailResult {
  const [data, setData] = useState<SubscriptionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setIsLoading(true);
    setIsError(false);
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    AgenciesService.getSubscriptionDetail(agencyId)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agencyId, tick]);

  return { data, isLoading, isError, refetch };
}
